import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CHANNEL_LABEL, OUTCOME_LABEL, OUTCOME_TONE, type Channel, type ConversationRow, type Outcome, type TouchChip } from '@coachrex/shared';
import { useConversations, useMe } from '../lib/api';
import { engagementTone, stamp, toneFg } from '../lib/format';
import { matchesRep, useRepFilter } from '../lib/repFilter';
import { Chip, ErrorBox, GradeBadge, PageHeader, Pills, RepFilter, Segmented, Skeleton } from '../components/ui';
import './conversations.css';

type Filter = 'all' | 'awaiting' | Outcome;
type Sort = 'latest' | 'engaged';
type Chan = 'all' | Exclude<Channel, 'form'>;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'awaiting', label: 'Awaiting reply' },
  { value: 'booked', label: OUTCOME_LABEL.booked },
  { value: 'follow_up', label: OUTCOME_LABEL.follow_up },
  { value: 'not_booked', label: OUTCOME_LABEL.not_booked },
  { value: 'missed', label: OUTCOME_LABEL.missed },
];

const matchFilter = (r: ConversationRow, f: Filter) => f === 'all' || (f === 'awaiting' ? r.awaiting : r.outcome === f);

export function Conversations() {
  const { data, error, isLoading } = useConversations();
  const rep = useRepFilter();
  const [params, setParams] = useSearchParams();
  // Reps see only their own leads: no rep filter or search.
  const isRep = useMe().data?.user.role === 'rep';
  const filter = (params.get('filter') as Filter) ?? 'all';
  const sort = (params.get('sort') as Sort) ?? 'latest';
  const chan = (params.get('channel') as Chan) ?? 'all';
  const q = params.get('q') ?? '';

  const setParam = (key: string, value: string, fallback: string) =>
    setParams(
      (p) => {
        if (value === fallback) p.delete(key);
        else p.set(key, value);
        return p;
      },
      { replace: true },
    );

  // Rep, channel and search narrow the set; the outcome pills then count within it.
  const scoped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.rows ?? []).filter(
      (r) =>
        matchesRep(rep.value, r.rep?.id) &&
        (chan === 'all' || r.touches.some((t) => t.channel === chan)) &&
        (!needle || r.name.toLowerCase().includes(needle) || (r.rep?.name.toLowerCase().includes(needle) ?? false)),
    );
  }, [data, rep.value, chan, q]);

  const rows = useMemo(() => {
    const list = scoped.filter((r) => matchFilter(r, filter));
    return sort === 'engaged' ? [...list].sort((a, b) => b.engagement.score - a.engagement.score) : list;
  }, [scoped, filter, sort]);

  const v = data?.volumes;
  const subtitle = v
    ? `Every call, text, email and form fill, grouped by lead. Last ${v.days} days: ${v.calls.toLocaleString('en-US')} calls, ${v.texts.toLocaleString('en-US')} texts, ${v.emails.toLocaleString('en-US')} emails.`
    : 'Every call, text, email and form fill, grouped by lead.';

  return (
    <div className="page">
      <PageHeader
        title="Conversations"
        subtitle={subtitle}
        actions={
          <>
            <RepFilter reps={data?.reps ?? []} />
            {!isRep && (
              <input className="search" type="search" value={q} onChange={(e) => setParam('q', e.target.value, '')} placeholder="Search by lead or rep" aria-label="Search by lead or rep" />
            )}
          </>
        }
      />
      <div className="toolbar">
        <Pills options={FILTERS.map((f) => ({ ...f, count: scoped.filter((r) => matchFilter(r, f.value)).length }))} value={filter} onChange={(f) => setParam('filter', f, 'all')} />
        <div className="toolbar__right">
          <Segmented
            label="Sort"
            options={[
              { value: 'latest', label: 'Latest' },
              { value: 'engaged', label: 'Most engaged' },
            ]}
            value={sort}
            onChange={(s) => setParam('sort', s, 'latest')}
          />
          <Segmented
            label="Channel"
            options={[
              { value: 'all', label: 'All channels' },
              { value: 'call', label: 'Calls' },
              { value: 'sms', label: 'SMS' },
              { value: 'email', label: 'Email' },
            ]}
            value={chan}
            onChange={(c) => setParam('channel', c, 'all')}
          />
        </div>
      </div>

      <section className="card card--list">
        <div className="list-head convo-grid">
          <span>Lead</span>
          <span>Touches</span>
          <span>Engagement</span>
          <span>Latest</span>
          <span>Outcome</span>
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} height={44} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="list-empty">No conversations match.</div>
        ) : (
          rows.map((r) => <Row key={r.leadId} r={r} timeZone={data!.timeZone} />)
        )}
      </section>
    </div>
  );
}

const TOUCH_HELP: Record<TouchChip['kind'], string> = {
  in: 'Calls from the lead',
  out: 'Calls from the team',
  manual: 'Sent by the lead or a rep',
  auto: 'Sent by a workflow, campaign or bulk action',
  all: 'Form fills',
};

function Row({ r, timeZone }: { r: ConversationRow; timeZone: string }) {
  const engFg = toneFg(engagementTone(r.engagement.label));
  const meta = [stamp(r.latest.at, timeZone), r.latest.who, CHANNEL_LABEL[r.latest.channel], r.latest.flag?.label].filter(Boolean).join(' · ');
  return (
    <Link to={`/conversations/${r.leadId}`} className="list-row convo-grid">
      <span className="convo-lead">
        <GradeBadge grade={r.grade} size={26} />
        <span className="convo-lead__text">
          <span className="convo-lead__name">{r.name}</span>
          <span className="convo-lead__rep">with {r.rep?.name ?? '—'}</span>
        </span>
      </span>
      <span className="convo-touches">
        {r.touches.map((t) => (
          <span key={`${t.channel}-${t.kind}`} className={`chan-chip${t.kind === 'auto' ? ' is-auto' : ''}`} title={TOUCH_HELP[t.kind]}>
            {t.label}
            {t.count > 1 ? ` ×${t.count}` : ''}
          </span>
        ))}
      </span>
      <span className="convo-eng">
        <span className="convo-eng__top">
          <span className="convo-eng__score">{r.engagement.score}</span>
          <span className="convo-eng__label" style={{ color: engFg }}>
            {r.engagement.label}
          </span>
        </span>
        <span className="bar">
          <span style={{ width: `${r.engagement.score}%`, background: engFg }} />
        </span>
      </span>
      <span className="convo-latest">
        <span className="convo-latest__text">{r.latest.text}</span>
        <span className="convo-latest__meta" style={{ color: r.awaiting ? 'var(--red-fg)' : undefined }}>
          {meta}
        </span>
      </span>
      <span>
        <Chip tone={OUTCOME_TONE[r.outcome]}>{OUTCOME_LABEL[r.outcome]}</Chip>
      </span>
    </Link>
  );
}
